// ============================================================================
// DAILY REWARDS WIDGET - PRODUCTION READY v2.0
// ============================================================================
// Features:
// - Auto-refresh when crossing midnight (daily) or Monday (weekly)
// - Live countdown timer that updates every second
// - Consistent US Eastern timezone
// - Touch-friendly, responsive, safe area aware
// - Toast notifications on reset
// ============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Progress,
  Badge,
  Icon,
  Collapse,
  useToast,
  Skeleton,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import {
  Trophy,
  Flame,
  Target,
  CheckCircle,
  Clock,
  Gift,
  ChevronDown,
  ChevronUp,
  Zap,
  Calendar,
  Star,
} from 'lucide-react';
import { POKER_COLORS } from '../../theme/designSystem';
import PokerButton from '../ui/PokerButton';
import { useSkillLevel } from '../../contexts/SkillLevelContext';

// ============================================================================
// ANIMATIONS
// ============================================================================

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(255, 215, 0, 0.4); }
  50% { box-shadow: 0 0 20px 4px rgba(255, 215, 0, 0.6); }
`;

// ============================================================================
// TIMEZONE UTILITIES - Must match backend exactly
// ============================================================================

/**
 * Get today's date in US Eastern timezone (YYYY-MM-DD)
 * CRITICAL: This must match the backend getTrainingToday() exactly
 */
const getEasternToday = (): string => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(new Date());
};

/**
 * Get Monday of current week in US Eastern timezone
 */
const getEasternWeekStart = (): string => {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  
  const easternDateStr = formatter.format(now);
  const easternDate = new Date(easternDateStr + 'T12:00:00');
  
  const dayOfWeek = easternDate.getDay();
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  easternDate.setDate(easternDate.getDate() - daysToSubtract);
  
  return formatter.format(easternDate);
};

/**
 * Get time remaining until midnight Eastern
 */
const getTimeUntilDailyReset = (): { hours: number; minutes: number; seconds: number; display: string } => {
  const now = new Date();
  
  // Get current Eastern time
  const easternNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  
  // Calculate midnight tonight Eastern
  const midnight = new Date(easternNow);
  midnight.setDate(midnight.getDate() + 1);
  midnight.setHours(0, 0, 0, 0);
  
  const diff = midnight.getTime() - easternNow.getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  
  return {
    hours,
    minutes,
    seconds,
    display: `${hours}h ${minutes}m`,
  };
};

/**
 * Get time remaining until Monday midnight Eastern
 */
const getTimeUntilWeeklyReset = (): string => {
  const now = new Date();
  const easternNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  
  const dayOfWeek = easternNow.getDay();
  const daysUntilMonday = dayOfWeek === 0 ? 1 : (dayOfWeek === 1 ? 7 : 8 - dayOfWeek);
  
  if (daysUntilMonday === 7) {
    // It's Monday, show hours until midnight
    const midnight = new Date(easternNow);
    midnight.setDate(midnight.getDate() + 1);
    midnight.setHours(0, 0, 0, 0);
    const diff = midnight.getTime() - easternNow.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    return `${hours}h`;
  }
  
  return `${daysUntilMonday} day${daysUntilMonday > 1 ? 's' : ''}`;
};

// ============================================================================
// TYPES
// ============================================================================

interface DailyRewardsWidgetProps {
  compact?: boolean;
  variant?: 'full' | 'compact';
  onRewardClaimed?: (amount: number) => void;
  isRestrictedState?: boolean; //  NEW: Show PlayCoins instead of PSS Coins

}

// ============================================================================
// CHALLENGE ITEM COMPONENT
// ============================================================================

interface ChallengeItemProps {
  title: string;
  current: number;
  target: number;
  completed: boolean;
  claimed?: boolean;
  pkCashReward: number;
  icon: React.ReactElement;
  compact?: boolean;
  isRestrictedState?: boolean; //  NEW

}

const ChallengeItem: React.FC<ChallengeItemProps> = ({
  title,
  current,
  target,
  completed,
  claimed = false,
  pkCashReward,
  icon,
  compact = false,
  isRestrictedState = false, //  NEW

}) => {
  const progress = Math.min((current / target) * 100, 100);
  const isClaimable = completed && !claimed;
  
  return (
    <Box
      p={compact ? 3 : 4}
      bg={completed ? `${POKER_COLORS.darkGreen}22` : 'blackAlpha.400'}
      borderRadius="xl"
      border="1px solid"
      borderColor={isClaimable ? POKER_COLORS.gold : completed ? POKER_COLORS.darkGreen : 'whiteAlpha.200'}
      animation={isClaimable ? `${pulseGlow} 2s ease-in-out infinite` : undefined}
      transition="all 0.3s"
      minH="68px"
    >
      <HStack justify="space-between" align="center">
        <HStack spacing={3} flex={1}>
          <Box
            p={2}
            bg={completed ? `${POKER_COLORS.darkGreen}33` : 'whiteAlpha.100'}
            borderRadius="lg"
          >
            {React.cloneElement(icon, {
              size: compact ? 20 : 24,
              color: completed ? POKER_COLORS.darkGreen : '#9CA3AF',
            })}
          </Box>
          <VStack align="start" spacing={1} flex={1}>
            <Text 
              color="white" 
              fontWeight="semibold" 
              fontSize={compact ? 'md' : 'lg'}
              lineHeight="1.2"
              noOfLines={2}
            >
              {title}
            </Text>
            <Text 
              color={completed ? POKER_COLORS.darkGreen : 'gray.400'} 
              fontSize={compact ? 'sm' : 'md'}
              fontWeight="medium"
            >
              {current}/{target} {completed ? ' Complete' : ''}

            </Text>
          </VStack>
        </HStack>
        
        <VStack spacing={0} align="end" minW="60px">
          <HStack spacing={1}>
            <Text 
              color={isClaimable ? POKER_COLORS.gold : completed ? POKER_COLORS.darkGreen : POKER_COLORS.gold} 
              fontWeight="bold"
              fontSize={compact ? 'lg' : 'xl'}
            >
              {claimed ? '' : isRestrictedState ? `+${(pkCashReward * 100).toLocaleString()}` : `+${pkCashReward}`}

            </Text>
            {!claimed && (
              <Text 
                color={isClaimable ? POKER_COLORS.gold : 'gray.400'} 
                fontSize={compact ? 'sm' : 'md'}
                fontWeight="semibold"
              >
                {isRestrictedState ? 'PC' : 'PSS'}
              </Text>
            )}
          </HStack>
        </VStack>
      </HStack>
      
      {!completed && (
        <Progress
          value={progress}
          size="sm"
          mt={3}
          colorScheme="green"
          bg="whiteAlpha.200"
          borderRadius="full"
          h="6px"
        />
      )}
    </Box>
  );
};

// ============================================================================
// MAIN WIDGET
// ============================================================================

const DailyRewardsWidget: React.FC<DailyRewardsWidgetProps> = ({
  compact = false,
  variant = 'full',
  onRewardClaimed,
  isRestrictedState = false, //  NEW

}) => {
  const toast = useToast();
  
  //  Helper for currency display

  const currencyLabel = isRestrictedState ? 'PlayCoins' : 'PSS Coins';
  const formatReward = (amount: number) => isRestrictedState 
    ? `+${(amount * 100).toLocaleString()} PC` 
    : `+$${amount}`;
  
  const {
    progress,
    dailyRewards,
    weeklyRewards,
    canClaimDaily,
    canClaimWeekly,
    claimDailyRewards,
    claimWeeklyRewards,
    getDailyProgress,
    getWeeklyProgress,
    isLoading,
    refreshProgress,
  } = useSkillLevel();
  
  const [isExpanded, setIsExpanded] = useState(!compact && variant === 'full');
  const [isClaiming, setIsClaiming] = useState(false);
  const [showWeekly, setShowWeekly] = useState(false);
  const [timeDisplay, setTimeDisplay] = useState(getTimeUntilDailyReset().display);
  
  // Track the date we initialized on to detect day change
  const initialDateRef = useRef(getEasternToday());
  const initialWeekRef = useRef(getEasternWeekStart());
  
  const dailyProgress = getDailyProgress();
  const weeklyProgress = getWeeklyProgress();

  // ============================================================================
  // LIVE TIMER + AUTO-REFRESH ON RESET
  // ============================================================================
  useEffect(() => {
    const checkAndUpdate = () => {
      // Update timer display
      const timeInfo = getTimeUntilDailyReset();
      setTimeDisplay(timeInfo.display);
      
      // Check if we've crossed into a new day (Eastern time)
      const currentDay = getEasternToday();
      const currentWeek = getEasternWeekStart();
      
      // Daily reset detection
      if (currentDay !== initialDateRef.current) {
        console.log('🌅 [DailyRewards] New day detected! Refreshing...');
        initialDateRef.current = currentDay;
        
        if (refreshProgress) {
          refreshProgress();
          toast({
            title: 'New Day!',

            description: 'Daily challenges have been reset. Good luck!',
            status: 'info',
            duration: 4000,
            isClosable: true,
          });
        }
      }
      
      // Weekly reset detection
      if (currentWeek !== initialWeekRef.current) {
        console.log('📅 [DailyRewards] New week detected! Refreshing...');
        initialWeekRef.current = currentWeek;
        
        if (refreshProgress) {
          refreshProgress();
          toast({
            title: 'New Week!',

            description: 'Weekly challenges have been reset.',
            status: 'info',
            duration: 4000,
            isClosable: true,
          });
        }
      }
    };
    
    // Check immediately
    checkAndUpdate();
    
    // Update timer every second for smooth countdown
    const timerInterval = setInterval(() => {
      setTimeDisplay(getTimeUntilDailyReset().display);
    }, 1000);
    
    // Check for reset every 30 seconds
    const resetCheckInterval = setInterval(checkAndUpdate, 30000);
    
    return () => {
      clearInterval(timerInterval);
      clearInterval(resetCheckInterval);
    };
  }, [refreshProgress, toast]);

  // Also check when dailyRewards changes (in case backend reset happened)
  useEffect(() => {
    if (dailyRewards?.lastClaimDate) {
      const serverDate = dailyRewards.lastClaimDate;
      const clientDate = getEasternToday();
      
      // If server date doesn't match client date, server might have stale data
      // or client crossed midnight - either way, refresh
      if (serverDate !== clientDate && refreshProgress) {
        console.log('🔄 [DailyRewards] Date mismatch detected. Server:', serverDate, 'Client:', clientDate);
        refreshProgress();
      }
    }
  }, [dailyRewards?.lastClaimDate, refreshProgress]);

  // ============================================================================
  // CLAIM HANDLERS
  // ============================================================================
  
  const handleClaimDaily = async () => {
    if (!canClaimDaily || isClaiming) return;
    
    setIsClaiming(true);
    try {
      const result = await claimDailyRewards();
      if (result.success) {
        const rewardText = isRestrictedState 
          ? `+${(result.pkCashEarned * 100).toLocaleString()} PlayCoins earned!`
          : `+$${result.pkCashEarned} PSS Coins earned!`;
        toast({
          title: 'Rewards Claimed!',

          description: rewardText,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
        onRewardClaimed?.(result.pkCashEarned);
      } else {
        toast({
          title: 'Could not claim rewards',
          description: result.error || 'Please try again',
          status: 'error',
          duration: 4000,
        });
      }
    } catch (err) {
      console.error('Claim error:', err);
      toast({
        title: 'Error claiming rewards',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsClaiming(false);
    }
  };

  const handleClaimWeekly = async () => {
    if (!canClaimWeekly || isClaiming) return;
    
    setIsClaiming(true);
    try {
      const result = await claimWeeklyRewards();
      if (result.success) {
        const rewardText = isRestrictedState 
          ? `+${(result.pkCashEarned * 100).toLocaleString()} PlayCoins earned!`
          : `+$${result.pkCashEarned} PSS Coins earned!`;
        toast({
          title: 'Weekly Rewards Claimed!',

          description: rewardText,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
        onRewardClaimed?.(result.pkCashEarned);
      } else {
        toast({
          title: 'Could not claim rewards',
          description: result.error || 'Please try again',
          status: 'error',
          duration: 4000,
        });
      }
    } catch (err) {
      toast({
        title: 'Error claiming rewards',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsClaiming(false);
    }
  };

  // ============================================================================
  // LOADING STATE
  // ============================================================================
  
  if (isLoading || !dailyRewards) {
    return (
      <Box
        bg={`${POKER_COLORS.darkTeal}cc`}
        borderRadius="xl"
        border="1px solid"
        borderColor={`${POKER_COLORS.cyan}33`}
        p={4}
      >
        <VStack spacing={4} align="stretch">
          <Skeleton height="24px" width="60%" />
          <Skeleton height="60px" />
          <Skeleton height="60px" />
          <Skeleton height="60px" />
        </VStack>
      </Box>
    );
  }

  // Get challenges with safe defaults
  const challenges = dailyRewards.challenges || dailyRewards.todaysChallenges || {
    playHands: { current: 0, target: 160, completed: false, claimed: false, pkCash: 1 },
    completeScenarios: { current: 0, target: 35, completed: false, claimed: false, pkCash: 1 },
    getCorrect: { current: 0, target: 25, completed: false, claimed: false, pkCash: 1 },
  };

  // ============================================================================
  // RENDER
  // ============================================================================
  
  return (
    <Box
      bg={`${POKER_COLORS.darkTeal}cc`}
      borderRadius="xl"
      border="1px solid"
      borderColor={canClaimDaily ? POKER_COLORS.gold : `${POKER_COLORS.cyan}33`}
      overflow="hidden"
      transition="all 0.3s"
      animation={canClaimDaily ? `${pulseGlow} 2s ease-in-out infinite` : undefined}
    >
      {/* Header - Always Visible */}
      <Box
        p={4}
        cursor="pointer"
        onClick={() => setIsExpanded(!isExpanded)}
        _hover={{ bg: 'whiteAlpha.50' }}
        _active={{ bg: 'whiteAlpha.100' }}
        transition="background 0.2s"
        minH="48px"
      >
        <HStack justify="space-between" align="center">
          <HStack spacing={3}>
            <Box
              p={2}
              bg={canClaimDaily ? `${POKER_COLORS.gold}33` : `${POKER_COLORS.cyan}22`}
              borderRadius="lg"
            >
              <Gift size={24} color={canClaimDaily ? POKER_COLORS.gold : POKER_COLORS.cyan} />
            </Box>
            <VStack align="start" spacing={0}>
              <HStack spacing={2}>
                <Text color="white" fontWeight="bold" fontSize="lg">
                  Daily Challenges
                </Text>
                {canClaimDaily && (
                  <Badge colorScheme="yellow" fontSize="xs" animation={`${pulseGlow} 2s infinite`}>
                    CLAIM
                  </Badge>
                )}
              </HStack>
              <Text color="gray.400" fontSize="sm" fontWeight="medium">
                {dailyProgress.completed}/3 complete
              </Text>
            </VStack>
          </HStack>
          
          <HStack spacing={3}>
            <VStack spacing={0} align="end">
              <HStack>
                <Clock size={14} color="gray" />
                <Text color="gray.400" fontSize="sm" fontWeight="medium">
                  {timeDisplay}
                </Text>
              </HStack>
            </VStack>
            <Icon
              as={isExpanded ? ChevronUp : ChevronDown}
              color="gray.400"
              boxSize={6}
            />
          </HStack>
        </HStack>
        
        {/* Progress bar */}
        <Progress
          value={dailyProgress.percentage}
          size="sm"
          colorScheme={dailyProgress.completed === 3 ? 'green' : 'cyan'}
          bg="whiteAlpha.200"
          borderRadius="full"
          h="6px"
          mt={3}
        />
      </Box>
      
      {/* Expandable Content */}
      <Collapse in={isExpanded} animateOpacity>
        <VStack spacing={3} p={4} pt={0} align="stretch">
          {/* Daily/Weekly Toggle */}
          <HStack 
            bg="blackAlpha.400" 
            borderRadius="lg" 
            p={1}
            spacing={0}
          >
            <Box
              flex={1}
              py={2}
              px={4}
              bg={!showWeekly ? POKER_COLORS.cyan : 'transparent'}
              color={!showWeekly ? 'black' : 'gray.400'}
              borderRadius="md"
              cursor="pointer"
              onClick={() => setShowWeekly(false)}
              transition="all 0.2s"
              textAlign="center"
              fontWeight="semibold"
              fontSize="md"
              minH="44px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <HStack spacing={2} justify="center">
                <Zap size={18} color={!showWeekly ? 'black' : 'gray'} />
                <Text>Daily</Text>
                {canClaimDaily && (
                  <Box w={2} h={2} bg={POKER_COLORS.gold} borderRadius="full" />
                )}
              </HStack>
            </Box>
            <Box
              flex={1}
              py={2}
              px={4}
              bg={showWeekly ? POKER_COLORS.gold : 'transparent'}
              color={showWeekly ? 'black' : 'gray.400'}
              borderRadius="md"
              cursor="pointer"
              onClick={() => setShowWeekly(true)}
              transition="all 0.2s"
              textAlign="center"
              fontWeight="semibold"
              fontSize="md"
              minH="44px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <HStack spacing={2} justify="center">
                <Calendar size={18} color={showWeekly ? 'black' : 'gray'} />
                <Text>Weekly</Text>
                {canClaimWeekly && (
                  <Box w={2} h={2} bg={POKER_COLORS.gold} borderRadius="full" />
                )}
              </HStack>
            </Box>
          </HStack>
          
          {/* Daily Challenges */}
          {!showWeekly && (
            <>
              <ChallengeItem
                title={challenges.playHands?.description || `Play ${challenges.playHands?.target || 60} training hands`}
                current={challenges.playHands?.current || 0}
                target={challenges.playHands?.target || 60}
                completed={(challenges.playHands?.current || 0) >= (challenges.playHands?.target || 60)}
                claimed={challenges.playHands?.claimed || false}
                pkCashReward={challenges.playHands?.pkCash || 3}
                icon={<Target />}
                compact={compact}
                isRestrictedState={isRestrictedState}
              />
              
              <ChallengeItem
                title={challenges.completeScenarios?.description || `Complete ${challenges.completeScenarios?.target || 18} scenarios`}
                current={challenges.completeScenarios?.current || 0}
                target={challenges.completeScenarios?.target || 18}
                completed={(challenges.completeScenarios?.current || 0) >= (challenges.completeScenarios?.target || 18)}
                claimed={challenges.completeScenarios?.claimed || false}
                pkCashReward={challenges.completeScenarios?.pkCash || 3}
                icon={<Star />}
                compact={compact}
                isRestrictedState={isRestrictedState}
              />
              
              <ChallengeItem
                title={challenges.getCorrect?.description || `Get ${challenges.getCorrect?.target || 12} correct answers`}
                current={challenges.getCorrect?.current || 0}
                target={challenges.getCorrect?.target || 12}
                completed={(challenges.getCorrect?.current || 0) >= (challenges.getCorrect?.target || 12)}
                claimed={challenges.getCorrect?.claimed || false}
                pkCashReward={challenges.getCorrect?.pkCash || 3}
                icon={<CheckCircle />}
                compact={compact}
                isRestrictedState={isRestrictedState}
              />
              
              {/* Streak Bonus */}
              {dailyProgress.completed === 3 && (
                <Box
                  p={4}
                  bg={`${POKER_COLORS.gold}22`}
                  borderRadius="lg"
                  border="2px dashed"
                  borderColor={POKER_COLORS.gold}
                >
                  <HStack justify="space-between">
                    <HStack spacing={2}>
                      <Flame size={22} color={POKER_COLORS.gold} />
                      <Text color={POKER_COLORS.gold} fontWeight="semibold" fontSize="md">
                        Streak Bonus (Day {(dailyRewards.streakDays || 0) + 1})
                      </Text>
                    </HStack>
                    <Text color={POKER_COLORS.gold} fontWeight="bold" fontSize="lg">
                      {isRestrictedState 
                        ? `+${(Math.min((dailyRewards.streakDays || 0) + 1, 7) * 25).toLocaleString()} PC`
                        : `+$${(Math.min((dailyRewards.streakDays || 0) + 1, 7) * 0.25).toFixed(2)} PSS`
                      }
                    </Text>
                  </HStack>
                </Box>
              )}
              
              {/* Playthrough Notice */}
              <Box
                p={3}
                bg="blackAlpha.300"
                borderRadius="md"
                borderLeft="3px solid"
                borderColor={POKER_COLORS.cyan}
              >
                <Text color="gray.300" fontSize="sm" fontWeight="medium">
                  {isRestrictedState 
                    ? 'ℹ PlayCoins rewards - free to play!'

                    : 'ℹ Training PSS Coins must be played through 3x before redemption'

                  }
                </Text>
              </Box>

              {/* Claim Button */}
              <PokerButton
                variant="primary"
                w="100%"
                size="lg"
                isDisabled={!canClaimDaily}
                isLoading={isClaiming}
                onClick={handleClaimDaily}
                bg={canClaimDaily ? POKER_COLORS.gold : undefined}
                color={canClaimDaily ? 'black' : undefined}
                leftIcon={<Gift size={20} />}
                minH="52px"
                fontSize="md"
                fontWeight="bold"
              >
                {canClaimDaily ? 'Claim Rewards' : 'Complete Challenges to Claim'}
              </PokerButton>
            </>
          )}
          
          {/* Weekly Challenges */}
          {showWeekly && weeklyRewards && (
            <>
              <HStack justify="space-between" mb={2}>
                <Text color="gray.300" fontSize="md" fontWeight="medium">
                  Resets in {getTimeUntilWeeklyReset()}
                </Text>
                <Badge colorScheme="purple" fontSize="sm" fontWeight="bold">
                  Up to {isRestrictedState 
                    ? `${((weeklyRewards.totalPKCashAvailable || 25) * 100).toLocaleString()} PC`
                    : `${weeklyRewards.totalPKCashAvailable || 25} PSS Coins`
                  }
                </Badge>
              </HStack>
              
              {(weeklyRewards.challenges || []).map((challenge: any) => (
                <ChallengeItem
                  key={challenge.id}
                  title={challenge.description}
                  current={challenge.current || 0}
                  target={challenge.target}
                  completed={(challenge.current || 0) >= challenge.target}
                  claimed={challenge.claimed || false}
                  pkCashReward={challenge.pkCashReward}
                  icon={
                    challenge.type === 'playHands' ? <Target /> :
                    challenge.type === 'scenarios' ? <Star /> :
                    challenge.type === 'dailyLogins' ? <Calendar /> :
                    <Trophy />
                  }
                  compact={compact}
                  isRestrictedState={isRestrictedState}
                />
              ))}
              
              <PokerButton
                variant="primary"
                w="100%"
                size="lg"
                isDisabled={!canClaimWeekly}
                isLoading={isClaiming}
                onClick={handleClaimWeekly}
                bg={canClaimWeekly ? 'purple.500' : undefined}
                leftIcon={<Trophy size={20} />}
                minH="52px"
                fontSize="md"
                fontWeight="bold"
              >
                {canClaimWeekly 
                  ? isRestrictedState 
                    ? `Claim ${((weeklyProgress.pkCashAvailable || 0) * 100).toLocaleString()} PC`
                    : `Claim ${weeklyProgress.pkCashAvailable || 0} PSS Coins`
                  : 'Complete Challenges'
                }
              </PokerButton>
            </>
          )}
        </VStack>
      </Collapse>
    </Box>
  );
};

// ============================================================================
// COMPACT VERSION FOR LOBBY
// ============================================================================

export const DailyRewardsCompact: React.FC<{ onClick?: () => void }> = ({ onClick }) => {
  const { dailyRewards, canClaimDaily, canClaimWeekly, getDailyProgress } = useSkillLevel();
  const dailyProgress = getDailyProgress();
  
  if (!dailyRewards) return null;
  
  const hasRewards = canClaimDaily || canClaimWeekly;
  
  return (
    <Box
      p={4}
      bg={hasRewards ? `${POKER_COLORS.gold}22` : 'blackAlpha.400'}
      borderRadius="lg"
      border="1px solid"
      borderColor={hasRewards ? POKER_COLORS.gold : 'whiteAlpha.200'}
      cursor="pointer"
      onClick={onClick}
      transition="all 0.2s"
      _hover={{ transform: 'scale(1.02)' }}
      _active={{ transform: 'scale(0.98)' }}
      minH="64px"
    >
      <HStack justify="space-between">
        <HStack spacing={3}>
          <Gift size={24} color={hasRewards ? POKER_COLORS.gold : 'gray'} />
          <VStack align="start" spacing={1}>
            <Text color="white" fontSize="md" fontWeight="semibold">
              Daily Challenges
            </Text>
            <Text color="gray.400" fontSize="sm" fontWeight="medium">
              {dailyProgress.completed}/3 complete
            </Text>
          </VStack>
        </HStack>
        
        {hasRewards ? (
          <Badge colorScheme="yellow" fontSize="sm" fontWeight="bold" animation={`${pulseGlow} 2s infinite`}>
            CLAIM
          </Badge>
        ) : (
          <HStack spacing={1}>
            {(dailyRewards.streakDays || 0) > 0 && (
              <>
                <Flame size={16} color={POKER_COLORS.gold} />
                <Text color={POKER_COLORS.gold} fontSize="sm" fontWeight="bold">
                  {dailyRewards.streakDays}
                </Text>
              </>
            )}
          </HStack>
        )}
      </HStack>
      
      <Progress
        value={dailyProgress.percentage}
        size="sm"
        mt={3}
        colorScheme={hasRewards ? 'yellow' : 'cyan'}
        bg="whiteAlpha.200"
        borderRadius="full"
        h="6px"
      />
    </Box>
  );
};

export default DailyRewardsWidget;